import React from "react";
import { Shield, Dumbbell, Crosshair, RefreshCw } from "lucide-react";
import { Band, Glow, Eyebrow, Reveal, PrimaryCTA } from "./shared";
import { LEGS_COMPARE_IMG } from "./content";

const FEATURES = [
  { Icon: Shield, t: "Pelvic Stabilization", d: "Supports a neutral pelvis position for better alignment and posture." },
  { Icon: Dumbbell, t: "Restore Strength", d: "Activates and strengthens your glutes, hips, and core naturally." },
  { Icon: Crosshair, t: "Improve Coordination", d: "Enhances muscle control and body awareness in every movement." },
  { Icon: RefreshCw, t: "Move With Confidence", d: "Reduce strain, prevent compensation, and get back to doing what you love." },
];

export function RestoreStrengthSection() {
  return (
    <Band id="restore-strength">
      <Glow className="top-10 right-1/4 w-[500px] h-[500px] bg-[#3B82F6]/8" />

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        {/* Anatomical comparison visual + native labels */}
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#060C1C] to-[#03080F] shadow-[0_24px_70px_rgba(0,0,0,0.55)]">
            <img
              src={LEGS_COMPARE_IMG}
              alt="Lower body alignment comparison — without support versus with the Back Pain Transformer"
              className="w-full object-cover"
              data-testid="restore-legs-image"
            />
            <div className="grid grid-cols-2 border-t border-white/10 divide-x divide-white/10">
              <div className="p-4 text-center">
                <div className="font-display text-sm font-bold uppercase tracking-[0.14em] text-white">Without Support</div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#EF4444] mt-1.5 leading-relaxed">
                  Nerve Pressure · Muscle Imbalance · Limited Strength
                </div>
              </div>
              <div className="p-4 text-center">
                <div className="font-display text-sm font-bold uppercase tracking-[0.14em] text-white">With Support</div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#22D3EE] mt-1.5 leading-relaxed">
                  Lifted Glutes · Neutral Pelvis · Restored Strength
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Headline + copy */}
        <Reveal delay={0.12}>
          <Eyebrow className="text-[#22D3EE]">The Science</Eyebrow>
          <h2 className="mt-3 font-display font-extrabold uppercase tracking-tight leading-[0.92]">
            <span className="block text-2xl sm:text-3xl text-white">Restore Your</span>
            <span className="block text-5xl sm:text-6xl lg:text-7xl italic text-[#3B82F6] drop-shadow-[0_0_28px_rgba(59,130,246,0.45)]">
              Strength
            </span>
          </h2>

          <div className="mt-5 h-px w-full bg-gradient-to-r from-[#22D3EE]/60 via-[#22D3EE]/20 to-transparent" />

          <p className="mt-5 font-display text-sm sm:text-base font-semibold uppercase tracking-[0.06em] text-[#C7D6EA]">
            Correct pelvic tilt. Relieve nerve pressure.{" "}
            <span className="text-[#22D3EE]">Move better. Feel stronger.</span>
          </p>

          <p className="mt-5 text-base text-[#B8C7DC] leading-relaxed">
            The <span className="text-[#22D3EE] font-semibold">Back Pain Transformer</span> lifts and supports the gluteus maximus. This alleviates gravitational pull on the sciatic nerve.
          </p>
          <p className="mt-4 text-base text-[#B8C7DC] leading-relaxed">
            It helps maintain a <span className="text-[#22D3EE] font-semibold">"neutral"</span> pelvis position which allows for proper muscle function — avoiding the numerous lower-body problems caused by pelvic tilt.
          </p>

          <div className="mt-8"><PrimaryCTA testid="restore-cta" /></div>
        </Reveal>
      </div>

      {/* Feature row */}
      <Reveal delay={0.1}>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map(({ Icon, t, d }) => (
            <div key={t} className="bps-card p-5 text-center" data-testid={`restore-feature-${t.split(" ")[0].toLowerCase()}`}>
              <div className="mx-auto w-11 h-11 rounded-xl bg-[#22D3EE]/12 border border-[#22D3EE]/25 flex items-center justify-center">
                <Icon size={20} className="text-[#22D3EE]" />
              </div>
              <div className="font-display font-semibold text-white uppercase tracking-wide text-sm mt-3">{t}</div>
              <div className="text-sm text-[#9AAEC6] mt-1.5 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </Reveal>

      {/* Bottom strip */}
      <Reveal delay={0.15}>
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] px-6 sm:px-8 py-6 flex items-center gap-5">
          <span className="shrink-0 w-12 h-12 rounded-xl bg-[#22D3EE]/12 border border-[#22D3EE]/25 flex items-center justify-center">
            <Shield size={24} className="text-[#22D3EE]" />
          </span>
          <div className="flex-1">
            <div className="font-display font-bold text-white uppercase tracking-wide text-xl sm:text-2xl lg:text-3xl xl:text-4xl leading-tight">
              Stronger foundation. Better movements. Lasting results.
            </div>
            <div className="text-sm sm:text-base lg:text-lg xl:text-xl uppercase tracking-[0.14em] text-[#22D3EE] mt-2">
              Retrain your body. Restore your strength. Reclaim your life.
            </div>
          </div>
        </div>
      </Reveal>
    </Band>
  );
}
